import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import {
  CtActivateActionsConfiguration,
  CtBinaryOperator,
  CtDatatableActionConfiguration,
  CtDatatableActionCustomConfiguration,
  CtDatatableActionsMenuConfiguration,
  CtDatatableConfiguration,
  CtDatatableMenuActionConfiguration,
  CtExportConfiguration,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelOperator,
  CtModelRouteData,
  CtModelService,
  MAT_RAISED_PRIMARY
} from "@ctsolution/ct-framework";
import { ActivatedRoute, Router } from "@angular/router";
import { DEFAULT_DATATABLE_CONFIGURATION } from "../../../_core/constants";
import { CTMGeneralService } from "../../../_core/lib/general.service";
import { WorkOrderRouteData } from "../../../_core/route-data/work-order.route-data";
import { CtManagerService } from "../../../_core/lib/ct-manager.service";
import { CTMWorkOrderViewType } from "../../../_core/enum/work-order-type";
import { WorkOrderService } from "../work-order.service";
import { LangDownloadFileConfiguration, WorkOrderRowsExportTypeVO } from "../../lang-download-file/lang-download-file.configuration";
import { ReportDownloadParameter } from "../../../_core/classes/report-download.parameter";
import { LanguageOption, WorkOrderDownloadMenuConfiguration, ExportTemplateOption } from "../../work-order-download-menu/work-order-download-menu.configuration";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { CtGenericDialogComponent, CtGenericDialogConfiguration } from "@ctsolution/ct-framework";
import { WorkOrderStatusEnum } from "../../../_core/enum/work-order-state";
import { DataRequest, MethodEnum } from "@ctsolution/ct-webapi";
import { CTConfigurationModel } from "../../../_core/classes/ct-configuration-model";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../../../../environments/environment";

@Component({
  selector: "app-work-order-list",
  template: `
    <ct-model *ngIf="configuration" [configuration]="configuration"></ct-model>

    <!-- Template per il bottone di download - supporta sia il nuovo componente che quello legacy -->
    <ng-template #downloadPdfButton let-value="value">

      <!-- Nuovo componente con menu avanzato (se enabled = true) -->
      <app-work-order-download-menu
        *ngIf="workOrderDownloadMenuConfiguration && workOrderDownloadMenuConfiguration.enabled"
        [configuration]="workOrderDownloadMenuConfiguration"
        [workOrderOid]="value?.Oid"
        [workOrderData]="value">
      </app-work-order-download-menu>

      <!-- Componente legacy per compatibilità (se enabled = false o configurazione non presente) -->
      <app-lang-download-file
        *ngIf="!workOrderDownloadMenuConfiguration || !workOrderDownloadMenuConfiguration.enabled"
        [configuration]="langDownloadFileConfiguration"
        (onDownload)="downloadPdf(value, $event)">
      </app-lang-download-file>

    </ng-template>
  `
})
export class WorkOrderListComponent implements OnInit {

  configuration: CtModelConfiguration<WorkOrderListComponent> | null = null;

  // Stato del toggle per nascondere le cancellate (utilizzato nella callback del custom toggle)
  private hideCancelled: boolean = true;

  langDownloadFileConfiguration: LangDownloadFileConfiguration = LangDownloadFileConfiguration
    .create()
    .setColor("primary")
    .setIcon("picture_as_pdf")
    .setButtonType('mat-icon-button')
    .setLanguage([
      {
        language: "Italiano",
        code: "it",
        type: "IT",
        icon: "it"
      }
    ]);

  workOrderDownloadMenuConfiguration: WorkOrderDownloadMenuConfiguration = WorkOrderDownloadMenuConfiguration
    .create()
    .setEnabled(false)
    .setMaxDocumentsToShow(5)
    .setColor("primary")
    .setIcon("picture_as_pdf")
    .setButtonType('mat-icon-button')
    .setGeneratedPdfLabel("PDF Generato Automaticamente")
    .setUploadedDocumentsLabel("Documenti Caricati")
    .setShowFileIcon(true)
    .setLanguages([
      {
        language: "Italiano",
        code: "it",
        type: "IT",
        icon: "it"
      }
    ]);

  depositRedirectCTA: CtDatatableActionConfiguration = new CtDatatableActionConfiguration()
    .setColor("primary")
    .setIcon("warehouse");

  CTMWorkOrderType: CTMWorkOrderViewType = CTMWorkOrderViewType.Standard;
  @ViewChild("downloadPdfButton") downloadPdfButton: TemplateRef<any> | null = null;

  // Configurazione per controllare la visibilità dell'export
  workOrderExportConfiguration: CTConfigurationModel = CTConfigurationModel.create();

  // Configurazione custom per la work-order-list (include enableConfigurationCodeMode)
  customWorkOrderListConfiguration: CTConfigurationModel = CTConfigurationModel.create().setVisible(false);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private general: CTMGeneralService,
    private managerService: CtManagerService,
    private workOrderService: WorkOrderService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private ctModelService: CtModelService<any>,
    private translate: TranslateService) {
  }

  async ngOnInit() {
    // ✅ FIX ExpressionChangedAfterItHasBeenCheckedError
    // Ritardiamo il setup con setTimeout per evitare che l'assegnazione di configuration
    // avvenga durante il ciclo di change detection corrente
    setTimeout(async () => {
      await this.setup();
    }, 0);
  }

  get isQuotation() {
    return this.CTMWorkOrderType === CTMWorkOrderViewType.Quotation;
  }

  /**
   * Callback invocata dal custom toggle del framework quando l'utente clicca sul toggle.
   * Gestisce l'attivazione/disattivazione del filtro per nascondere gli ordini cancellati.
   *
   * IMPORTANTE: Questa callback deve SOLO modificare i filterValues.
   * Il framework si occupa automaticamente di ricaricare i dati dopo questa callback.
   *
   * @param active - Stato del toggle (true = nascondi cancellate, false = mostra tutte)
   */
  private onToggleHideCancelled(active: boolean): void {


    // Aggiorna lo stato interno
    this.hideCancelled = active;

    // Gestisci il filtro nei filterValues
    if (active) {

      // TOGGLE ON: Nascondi ordini cancellati
      // Aggiungi o aggiorna il filtro per escludere gli ordini con State.Type = Cancelled
      this.configuration!.filterValues.upsertConstantFilter(
        CTModelDatatableFilter.create()
          .setField('State.Type')
          .setValue(WorkOrderStatusEnum.Cancelled)
          .setOperatorType(CtBinaryOperator.NotEqual)
      );


    } else {

      // TOGGLE OFF: Mostra tutti gli ordini (anche cancellati)
      // Rimuovi il filtro sul campo State.Type
      const removed = this.configuration!.filterValues.removeConstantByField('State.Type');


    }

    // NOTA: NON serve chiamare setup() o fetchData()!
    // Il framework chiama automaticamente fetchData() dopo questa callback:
    // - Durante inizializzazione: setupContainer() → callback → fetchData automatica
    // - Al click utente: toggleCustomFilter() → callback → fetchData() automatica

  }

  /**
   * Gestisce la navigazione verso la edit controllando se mostrare la snackbar informativa.
   * La snackbar appare quando:
   * - enableConfigurationCodeMode è TRUE
   * - Il record è una commessa firmata (QuotationAcceptAt != null O Quotation = false)
   * - Siamo nella lista delle quotation (URL contiene 'quotations')
   *
   * Quando la snackbar viene mostrata, naviga verso il route STANDARD della work order (senza 'quotations').
   *
   * @param value - Il record della work order/quotation
   * @param controllerRoute - Il route verso cui navigare (può contenere 'quotations')
   */
  private navigateToEdit(value: any, controllerRoute: string[]): void {

    // Verifica se siamo nella lista delle quotation controllando l'URL
    const isInQuotationList = this.router.url.includes('quotations');

    // Verifica se il record è una commessa firmata
    // Una commessa è firmata se:
    // - QuotationAcceptAt ha una data (offerta accettata)
    // - Oppure Quotation è false (non è mai stata un'offerta)
    const isSignedWorkOrder = value.QuotationAcceptAt != null || value.Quotation === false;


    // ⚠️ Se tutte le condizioni sono vere, mostra snackbar E cambia il route
    if (this.customWorkOrderListConfiguration.enableConfigurationCodeMode &&
        isInQuotationList &&
        isSignedWorkOrder) {

      console.log('[NavigateToEdit] ⚠️ Mostra snackbar: offerta firmata');

      // Mostra snackbar informativa (5 secondi)
      this.snackBar.open(
        "L'offerta è stata firmata e confermata. Stai visualizzando il dettaglio della commessa.",
        'OK',
        {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        }
      );

      // ✅ Naviga verso il route STANDARD (senza 'quotations')
      // Otteniamo il route standard della work order
      const standardRoute = this.workOrderService.getRoutePathController(CTMWorkOrderViewType.Standard);
      console.log('[NavigateToEdit] standardRoute (modificato):', standardRoute);

      this.general.navigateOnCTModelRouter(standardRoute, "edit", {
        value,
        configuration: this.configuration
      });

    } else {

      // Navigazione normale con il route originale
      this.general.navigateOnCTModelRouter(controllerRoute, "edit", {
        value,
        configuration: this.configuration
      });

    }
  }

  /**
   * Gestisce la cancellazione con modale di conferma personalizzata
   */
  private async onDeleteWorkOrder(event: any): Promise<void> {
    // ✅ Verifica se l'ordine è già cancellato
    if (event?.State?.Type === WorkOrderStatusEnum.Cancelled) {
      // Mostra modale informativa
      this.dialog.open(CtGenericDialogComponent, {
        data: {
          configuration: CtGenericDialogConfiguration.create()
            .setTitle('Attenzione')
            .setContent('Risulta già in stato cancellato')
            .setSubmitLabel('Chiudi')
        },
        maxWidth: '500px',
        minWidth: '300px'
      });
      return;
    }

    // Apri la modale di conferma personalizzata
    const dialogRef = this.dialog.open(CtGenericDialogComponent, {
      data: {
        configuration: CtGenericDialogConfiguration.create()
          .setTitle('Conferma cancellazione')
          .setContent('Sei sicuro di voler cambiare lo stato in cancellata?')
          .setCancelLabel('Annulla')
          .setSubmitLabel('Conferma')
      },
      maxWidth: '500px',
      minWidth: '300px'
    });

    // Aspetta la risposta dell'utente
    const confirmed = await dialogRef.afterClosed().toPromise();

    // Se l'utente ha confermato, procedi con la cancellazione
    if (confirmed) {
      // Chiama il servizio del framework con skipConfirm = true
      const result = await this.ctModelService.delete(
        this.configuration!,
        event,
        true // skipConfirm = true
      );

      // Se la cancellazione è andata a buon fine, ricarica la lista
      if (result) {
        result.subscribe({
          next: () => {
            // Ricarica la configurazione
            this.configuration = null;
            setTimeout(async () => {
              await this.setup();
            }, 0);
          },
          error: (error) => {
            console.error('Errore durante la cancellazione:', error);
          }
        });
      }
    }
  }

  async setup() {

    this.CTMWorkOrderType = await this.workOrderService.getRouteWorkOrderType(this.route);

    await this.managerService.getComponentOverride("workOrder", "langDownloadFileConfiguration", this.langDownloadFileConfiguration).then((component) => {
      return;
    });

    await this.managerService.getComponentOverride("workOrder", "workOrderDownloadMenuConfiguration", this.workOrderDownloadMenuConfiguration).then((component) => {
      return;
    });

    // 🔍 DEBUG: verifica che l'override abbia riversato l'array exportTemplates dal JSON.
    // Se stampa i 3 oggetti -> override ok; se stampa [] -> il merge non copia gli array.
    console.log('[DTS] exportTemplates:', this.workOrderDownloadMenuConfiguration.exportTemplates);

    // ✅ Recupera la configurazione custom per la work-order-list (include enableConfigurationCodeMode)
    await this.managerService.getComponentOverride("workOrder", "customWorkOrderListConfiguration", this.customWorkOrderListConfiguration).then((component) => {
      return;
    });

    // 🔵 AMERI-ONLY: ripristina la colonna "Quotation" (Progetto/Offerta) nella lista.
    // Il flag quotationVisible arriva dalla config server (SiteConfiguration/MenuJson) e per
    // ameri non è impostato. ameri riusa l'app di ct-manager e si distingue solo tramite
    // environment.projectName (Ameri / AmeriTest / AmeriDemo): forziamo il flag solo per quel build,
    // senza impattare gli altri client.
    if ((environment.projectName ?? '').toLowerCase().startsWith('ameri')) {
      this.customWorkOrderListConfiguration.quotationVisible = true;
    }

    // ✅ Recupera la configurazione override per workOrder export
    await this.managerService.getComponentOverride("workOrder", "workOrderExportConfiguration", this.workOrderExportConfiguration).then((component) => {
      return;
    });

    const controllerRoute = this.workOrderService.getRoutePathController(this.CTMWorkOrderType);

    if (this.customWorkOrderListConfiguration.visible) {
      console.log('[WorkOrderList SETUP] ➡️ Usando setCustomConfiguration');
      this.setCustomConfiguration(controllerRoute);
    }
    else {
      console.log('[WorkOrderList SETUP] ➡️ Usando setDefaultConfiguration');
      this.setDefaultConfiguration(controllerRoute);

    }


    this.route
      .data
      .subscribe(data => {
        if (this.isQuotation) data['title'] = "CT_MENU.quotations";
      });

    // ⚠️ NOTA: La gestione del ConfigurationCode per le Quotation
    // è stata spostata dentro setDefaultConfiguration() e setCustomConfiguration()
    // per supportare la nuova modalità enableConfigurationCodeMode
  }

  setDefaultConfiguration(controllerRoute: string[]) {

    // ✅ Ricrea la datatableConfiguration ogni volta
    const datatableConfiguration: CtDatatableConfiguration = DEFAULT_DATATABLE_CONFIGURATION();

    const activateActions = CtActivateActionsConfiguration.create()
      .setDoubleClickCB(event => this.navigateToEdit(event, controllerRoute));

    datatableConfiguration.setActivateActionsConfiguration(activateActions);

    const config = CtModelConfiguration
      .create<WorkOrderListComponent>()
      .setRouteData(WorkOrderRouteData())
      .setCTDatatableConfiguration(datatableConfiguration)
      .setOnCreate(() => this.general.navigateOnCTModelRouter(controllerRoute, "create"))
      .setOnEdit(value => this.navigateToEdit(value, controllerRoute))
      .setOnDelete(value => this.onDeleteWorkOrder(value))
      .setOperations([
        // ✅ Create: solo se enableConfigurationCodeMode è FALSE o siamo in Quotation
        // Se enableConfigurationCodeMode è TRUE e siamo in Standard, NON mostriamo Create
        ...(!this.customWorkOrderListConfiguration.enableConfigurationCodeMode || this.isQuotation ? [CtModelOperator.Create] : []),
        CtModelOperator.Delete,
        CtModelOperator.FilterCustomToggle,  // ✅ Abilita il custom toggle per nascondere cancellate
        ...(this.workOrderExportConfiguration.exportExcelEnabled ? [CtModelOperator.Export] : [])  // ✅ Abilita export solo se visible
      ])

      // =================================================================
      // CONFIGURAZIONE FILTER CUSTOM TOGGLE
      // =================================================================
      // Configurazione del custom toggle per nascondere gli ordini cancellati
      .setCustomToggleLabel('Nascondi cancellate')  // Label del toggle
      .setCustomToggleInitialState(true)  // Stato iniziale: nascondi cancellate
      .setCustomTogglePersistState(true)  // Abilita persistenza in sessionStorage (default)
      .setOnCustomToggle((active: boolean) => this.onToggleHideCancelled(active))  // Callback

      .setFieldsCustomTemplates([
        {
          prop: 'QuotationAcceptAt',
          visible: this.isQuotation
        },
        {
          prop: 'StartAt',
          visible: !this.isQuotation
        },
        {
          prop: 'ExpiredAt',
          visible: !this.isQuotation
        },
        {
          prop: 'TotalAmount',
          visible: this.isQuotation
        },
        { prop: 'Quotation', visible: this.customWorkOrderListConfiguration.quotationVisible }

      ]);

    // ✅ Configurazione Export basata sul tipo di WorkOrder (Quotation o Standard)
    // SOLO se workOrderExportConfiguration.exportExcelEnabled è true
    if (this.workOrderExportConfiguration.exportExcelEnabled) {
      config.setExportConfiguration(
        CtExportConfiguration
          .create()
          // Action backend: ExportExcel_WorkOrder_Quotation o ExportExcel_WorkOrder
          .setAction(this.isQuotation ? 'ExportExcel_WorkOrder_Quotation' : 'ExportExcel_WorkOrder')
          // Label bottone export
          .setLabel('Esporta')
          // Icona bottone
          .setIcon('download')
          // Nome file per il download (senza estensione - valorizzato tramite chiave i18n)
          .setFileName(this.translate.instant(this.isQuotation ? 'WORK_ORDER.EXPORT_FILE_NAME_QUOTATION' : 'WORK_ORDER.EXPORT_FILE_NAME_STANDARD'))
          // Colore bottone blu
          .setMaterialOptions(MAT_RAISED_PRIMARY)
          // Esporta tutti i record ignorando la paginazione
          .setIncludePagination(false)
      );
    }

    datatableConfiguration
      .setCustomDatatableActionConfiguration(
        CtDatatableActionCustomConfiguration
          .create()
          .setTemplateOutlet(this.downloadPdfButton))
      .setDeleteActionEnabled(true);

    // =================================================================
    // GESTIONE FILTRI: MODALITÀ STANDARD vs CONFIGURATION CODE
    // =================================================================
    // Se enableConfigurationCodeMode è TRUE e siamo in QUOTATION:
    // - NON aggiungiamo filtri client-side
    // - Passiamo ConfigurationCode nel body della request
    //
    // Se enableConfigurationCodeMode è FALSE o siamo in STANDARD:
    // - Aggiungiamo filtri client-side come prima
    // - Per Quotation: impostiamo il customFieldsRequest (come prima)



    if (this.customWorkOrderListConfiguration.enableConfigurationCodeMode && this.isQuotation) {

      // ✅ MODALITÀ CONFIGURATION CODE per QUOTATION
      // NON aggiungiamo filtri constant, il backend filtrerà tramite ConfigurationCode
      console.log('[WorkOrderList] Modalità ConfigurationCode abilitata per Quotation');

      // ⚠️ NOTA IMPORTANTE:
      // In questa modalità, passiamo configurationCode come query param.
      // Il RouteData viene modificato per includere il query param nelle request.
      // Il backend riceverà: GET /api/WorkOrder/List?configurationCode=Quotation

      // Creiamo un RouteData custom con il query param configurationCode
      const customRouteData = CtModelRouteData
        .create()
        .setController(WorkOrderRouteData().controller!)

      config.setRouteData(customRouteData);

      // Impostiamo anche per Fields (già era così)
      const customFieldsRequest = new DataRequest()
        .setController([WorkOrderRouteData().controller!])
        .setMethod(MethodEnum.GET)
        .setAction('Fields')
        .setQueryParams({
          configurationCode: 'Quotation'
        });

      config.setGetFieldsDataRequest(customFieldsRequest);

    } else {

      // ✅ MODALITÀ STANDARD con filtri client-side
      let constantFilters;

      switch (this.CTMWorkOrderType) {

        case CTMWorkOrderViewType.Quotation:
          constantFilters = new Array<CTModelDatatableFilter>(
            this.getCTModelDatatableFilter<Array<any>>('Quotation = True and QuotationAcceptAt is null', [], CtBinaryOperator.AndOperator));
          break;

        case CTMWorkOrderViewType.Standard:
        default:
          constantFilters = new Array<CTModelDatatableFilter>(
            this.getCTModelDatatableFilter<Array<any>>('(Quotation = True and QuotationAcceptAt is not null) or Quotation = False', [], CtBinaryOperator.AndOperator));
          break;

      }

      constantFilters.forEach(filter => config.filterValues.addConstantFilter(filter));

      // ✅ Per le Quotation in modalità standard, impostiamo customFieldsRequest come prima
      if (this.isQuotation) {
        const customFieldsRequest = new DataRequest()
          .setController([WorkOrderRouteData().controller!])
          .setMethod(MethodEnum.GET)
          .setAction('Fields')
          .setQueryParams({
            configurationCode: 'Quotation'
          });

        config.setGetFieldsDataRequest(customFieldsRequest);
      }

    }

    // ⚠️ IMPORTANTE: NON aggiungere più il filtro qui manualmente!
    // Il filtro per nascondere le cancellate viene gestito automaticamente dalla callback
    // del custom toggle (onToggleHideCancelled) che viene invocata durante setupContainer()
    // PRIMA della fetch automatica.
    //
    // La callback verrà invocata automaticamente in due momenti:
    // 1. Durante l'inizializzazione (setupContainer) con lo stato salvato o iniziale
    // 2. Al click dell'utente sul toggle
    //
    // In entrambi i casi, la callback modificherà this.configuration.filterValues
    // aggiungendo o rimuovendo il filtro "State.Type != Cancelled"

    this.configuration = config;
  }

  setCustomConfiguration(controllerRoute: string[]) {

    const datatableConfiguration: CtDatatableConfiguration = DEFAULT_DATATABLE_CONFIGURATION();

    const activateActions = CtActivateActionsConfiguration.create()
      .setDoubleClickCB(event => this.navigateToEdit(event, controllerRoute));

    datatableConfiguration.setActivateActionsConfiguration(activateActions);

    const config = CtModelConfiguration
      .create<WorkOrderListComponent>()
      .setRouteData(WorkOrderRouteData())
      .setCTDatatableConfiguration(datatableConfiguration)
      .setOnCreate(() => this.general.navigateOnCTModelRouter(controllerRoute, "create"))
      .setCustomToggleLabel('Nascondi cancellate')  // Label del toggle
      .setCustomToggleInitialState(true)  // Stato iniziale: nascondi cancellate
      .setCustomTogglePersistState(true)  // Abilita persistenza in sessionStorage (default)
      .setOnCustomToggle((active: boolean) => this.onToggleHideCancelled(active))  // Callback

      .setFieldsCustomTemplates([
        {
          prop: 'QuotationAcceptAt',
          visible: this.isQuotation
        },
        {
          prop: 'StartAt',
          visible: !this.isQuotation
        },
        {
          prop: 'ExpiredAt',
          visible: !this.isQuotation
        },
        {
          prop: 'TotalAmount',
          visible: this.isQuotation
        },
        { prop: 'Quotation', visible: this.customWorkOrderListConfiguration.quotationVisible }
      ]);

    // =================================================================
    // MENU AZIONI (⋮) — voci di download
    // =================================================================
    // Se in config è presente exportTemplates (es. DTS: Informativa/Modulo/Registro),
    // costruiamo una voce per ciascun template e passiamo exportCustomTemplateType al backend.
    // Altrimenti manteniamo il comportamento storico con la singola voce "Download PDF".
    const actionsMenu = CtDatatableActionsMenuConfiguration.create()
      .setMaxVisibleActions(6);

    const exportTemplates = this.workOrderDownloadMenuConfiguration?.exportTemplates ?? [];

    if (exportTemplates.length > 0) {
      // DTS: una voce per ciascun template custom (Informativa/Modulo/Registro)
      // Visibili SOLO sulle righe di tipo PRIVACY (ExtraFields.String1 === 'PRIVACY')
      exportTemplates.forEach(template => {
        actionsMenu.addAction(
          CtDatatableMenuActionConfiguration.create()
            .setIcon(template.icon || 'picture_as_pdf')
            .setLabel(template.label)
            .setVisiblePredicate((row: any) => row?.ExtraFields?.String1 === 'PRIVACY')
            .setCallback((value: any) => this.downloadTemplatePdf(value, template))
        );
      });
    } else {
      // Comportamento storico: singola voce "Download PDF"
      actionsMenu.addAction(
        CtDatatableMenuActionConfiguration.create()
          .setIcon('picture_as_pdf')
          .setLabel('Download PDF')
          .setCallback((value: any) => this.downloadGeneratedPdf(value))
      );
    }

    actionsMenu
      .addAction(
        CtDatatableMenuActionConfiguration.create()
          .setIcon('edit')
          .setLabel('Modifica')
          .setCallback((value) => this.navigateToEdit(value, controllerRoute))
      )
      .addAction(
        CtDatatableMenuActionConfiguration.create()
          .setIcon('delete')
          .setLabel('Elimina')
          .setCallback((event) => this.onDeleteWorkOrder(event))
      );

    datatableConfiguration.setActionsMenuConfiguration(actionsMenu);



    if (this.customWorkOrderListConfiguration.enableConfigurationCodeMode && this.isQuotation) {

      // ✅ MODALITÀ CONFIGURATION CODE per QUOTATION
      console.log('[WorkOrderList CUSTOM] Modalità ConfigurationCode abilitata per Quotation');

      // Creiamo un RouteData custom con il query param configurationCode
      const customRouteData = CtModelRouteData
        .create()
        .setController(WorkOrderRouteData().controller!)

      config.setRouteData(customRouteData);



    } else {

      // ✅ MODALITÀ STANDARD con filtri client-side
      let constantFilters;

      switch (this.CTMWorkOrderType) {

        case CTMWorkOrderViewType.Quotation:
          constantFilters = new Array<CTModelDatatableFilter>(
            this.getCTModelDatatableFilter<Array<any>>('Quotation = True and QuotationAcceptAt is null', [], CtBinaryOperator.AndOperator));
          break;

        case CTMWorkOrderViewType.Standard:
        default:
          constantFilters = new Array<CTModelDatatableFilter>(
            this.getCTModelDatatableFilter<Array<any>>('(Quotation = True and QuotationAcceptAt is not null) or Quotation = False', [], CtBinaryOperator.AndOperator));
          break;

      }

      constantFilters.forEach(filter => config.filterValues.addConstantFilter(filter));

    }

    // ⚠️ IMPORTANTE: NON aggiungere più il filtro qui manualmente!
    // Il filtro per nascondere le cancellate viene gestito automaticamente dalla callback
    // del custom toggle (onToggleHideCancelled) che viene invocata durante setupContainer()
    // PRIMA della fetch automatica.
    //
    // La callback verrà invocata automaticamente in due momenti:
    // 1. Durante l'inizializzazione (setupContainer) con lo stato salvato o iniziale
    // 2. Al click dell'utente sul toggle
    //
    // In entrambi i casi, la callback modificherà this.configuration.filterValues
    // aggiungendo o rimuovendo il filtro "State.Type != Cancelled"

    config
      .initialized()
      .subscribe(() => this.configuration?.setOperations([
        // ✅ Create: solo se enableConfigurationCodeMode è FALSE o siamo in Quotation
        // Se enableConfigurationCodeMode è TRUE e siamo in Standard, NON mostriamo Create
        ...(!this.customWorkOrderListConfiguration.enableConfigurationCodeMode || this.isQuotation ? [CtModelOperator.Create] : []),
        CtModelOperator.FilterCustomToggle,
        ...(this.workOrderExportConfiguration.exportExcelEnabled ? [CtModelOperator.Export] : [])  // ✅ Abilita export solo se exportExcelEnabled è true
      ]));

    // ✅ Configurazione Export basata sul tipo di WorkOrder (Quotation o Standard)
    // SOLO se workOrderExportConfiguration.exportExcelEnabled è true
    if (this.workOrderExportConfiguration.exportExcelEnabled) {
      config.setExportConfiguration(
        CtExportConfiguration
          .create()
          // Action backend: ExportExcel_WorkOrder_Quotation o ExportExcel_WorkOrder
          .setAction(this.isQuotation ? 'ExportExcel_WorkOrder_Quotation' : 'ExportExcel_WorkOrder')
          // Label bottone export
          .setLabel('Esporta')
          // Icona bottone
          .setIcon('download')
          // Nome file per il download (senza estensione - valorizzato tramite chiave i18n)
          .setFileName(this.translate.instant(this.isQuotation ? 'WORK_ORDER.EXPORT_FILE_NAME_QUOTATION' : 'WORK_ORDER.EXPORT_FILE_NAME_STANDARD'))
          // Colore bottone blu
          .setMaterialOptions(MAT_RAISED_PRIMARY)
          // Esporta tutti i record ignorando la paginazione
          .setIncludePagination(false)
      );
    }

    this.configuration = config;

  }

  downloadPdf(value?: { Oid: number | null }, lang?: any) {

    if (!value?.Oid) return;

    const parameter: ReportDownloadParameter = ReportDownloadParameter
      .create()
      .setWorkOrderOid(value.Oid)
      .setTemplateLanguage(lang.code ?? 'it')
      .setRowType(lang.rowType ?? WorkOrderRowsExportTypeVO.All)
      .setDirectDownload(false)
      .setOpenInBrowser(true)
      .setAction('WorkOrderPdf')
      .setLabelDownload(`WorkOrder${value.Oid}`);

    this.workOrderService.downloadWorkOrderPDF(parameter);

  }

  private getCTModelDatatableFilter = <T>(field: string, value: T, operator: CtBinaryOperator) => CTModelDatatableFilter
    .create()
    .setField(field)
    .setValue(value).setOperatorType(operator);

  private downloadGeneratedPdf(value: any, language?: LanguageOption): void {
    if (!value.Oid) {
      console.error('WorkOrderDownloadMenuComponent: impossibile scaricare il PDF, workOrderOid non definito');
      return;
    }

    // Prepara i parametri per il download del PDF generato
    // Usa la lingua selezionata o la prima lingua configurata come fallback
    const languageCode = 'it';
    const rowType = WorkOrderRowsExportTypeVO.All;

    const parameter: ReportDownloadParameter = ReportDownloadParameter
      .create()
      .setWorkOrderOid(value.Oid)
      .setTemplateLanguage(languageCode)
      .setRowType(rowType)
      .setDirectDownload(false)
      .setOpenInBrowser(true)
      .setAction('WorkOrderPdf')
      .setLabelDownload(`WorkOrder${value.Oid}`);

    // Usa il service del WorkOrder per il download
    this.workOrderService.downloadWorkOrderPDF(parameter);
  }

  /**
   * Scarica il PDF di un template custom (es. DTS: Informativa/Modulo/Registro).
   * Valorizza exportCustomTemplateType nel ReportDownloadParameter, che viene
   * inoltrato al backend tramite lo spread dei query param (getAnyPDF).
   */
  private downloadTemplatePdf(value: any, template: ExportTemplateOption): void {
    if (!value?.Oid) {
      console.error('WorkOrderList: impossibile scaricare il PDF, Oid non definito');
      return;
    }

    const parameter: ReportDownloadParameter = ReportDownloadParameter
      .create()
      .setWorkOrderOid(value.Oid)
      .setTemplateLanguage(template.languageCode ?? 'it')
      .setRowType(template.rowType ?? WorkOrderRowsExportTypeVO.All)
      .setDirectDownload(false)
      .setOpenInBrowser(true)
      .setAction('WorkOrderPdf')
      .setLabelDownload(`WorkOrder${value.Oid}`)
      .setExportCustomTemplateType(template.exportCustomTemplateType);

    this.workOrderService.downloadWorkOrderPDF(parameter);
  }

}
